import React from "react";
import styled from "styled-components";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";

import { ActionDialog, ContourBackdrop } from "shared/ui";
import TableEditor, { TOOLBAR_DISABLE_CASES } from "shared/ui/TableEditor";

import { ReactComponent as OpenIcon } from "@mdi/svg/svg/arrow-right-bottom.svg";

const trDims = [
  ["id", "Ссылка", "Memo", 300],
  ["value", "Наименование", "Memo", 300],
  ["report", "Отчет", "Memo"],
  ["project", "Проект", "Memo"],
].map(([id, title, type, minWidth]) => ({
  id,
  styles: {
    minWidth,
  },
  properties: {
    title,
    name: id,
    axis: "data",
    position: 0,
    sortOrder: "none",
    filter: [],
    textFilter: {},
    type: type,
    isEditable: false,
  },
}));


const yearTrDims = [
  ["year", "Год", "Memo", 400],
].map(([id, title, type, minWidth]) => ({
  id,
  styles: {
    minWidth,
  },
  properties: {
    title,
    name: id,
    axis: "data",
    position: 0,
    sortOrder: "none",
    filter: [],
    textFilter: {},
    type: type,
    isEditable: false,
  },
}));

function panelTitle(panel, haveYear, valueOnly) {
  let title = "";

  if (valueOnly) return panel.value || "Панель";

  if (haveYear) {
    title += panel.value || "Панель";
    if (panel.year) title += ` (${panel.year})`;
    return title;
  }

  title += panel.value || "Панель";
  if (panel.report) title += ` (${panel.report})`;
  if (panel.project) title += ` (${panel.project})`;
  if (panel.number) title += ` (${panel.number})`;
  return title;
} 

function Panels({ onOk, registry, data }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const api = useSelector(state => state.API);

  const [panels, setPanels] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [haveYear, setHaveYear] = React.useState(false);
  const [tableData, setTableData] = React.useState([]);
  const [sessionRowsFired, setSessionRowsFired] = React.useState(false);
  const [changeRowsHandler, setRowsHandler] = React.useState(null);

  const sessionRows = React.useMemo(() => sessionStorage.getItem(data.code), [data]);

  const handleDashboardOpen = React.useCallback(({ rows, valueOnly }) => {
    const data = rows.map((id) => panels.find((panel) => panel.id === id));
    
    data.forEach((panel, index) => {
      setTimeout(() => {
        dispatch({ type: "SET_CURRENT", value: { current: { 
          id: panel.id,
          properties: { title: panelTitle(panel, haveYear, valueOnly), hint: null, icon: null },
          type: "dashboard"
        }}})
      }, 50 * index);
    })

    onOk();
  }, [dispatch, onOk, panels, haveYear]);
  
  const loadPanels = React.useCallback(async (row) => {
    const foundColumn = registry.widget[0]?.field_group[0]?.field?.find(({ field_type }) => field_type === "bi_project");
    let urls = row[foundColumn?.source];
    let value = row.value;

    if (!urls) {
      const data = await api.send("registries/list", {
        page_size: 50,
        page_number: 0,
        sort_order: [],
        code: "dashboard_registry",
        filter: [[null, "indicator", "=", row.code, false, null, "AND"]],
      });
      
      if (!Array.isArray(data) || data.length === 0) {
        onOk();
        enqueueSnackbar("Панель не найдена", { variant: "error" });
        return;
      }

      urls = data[0].url;
      if (data[0].value) value = data[0].value;
    }
    
    if (!Array.isArray(urls) || urls.length === 0) {
      onOk();
      enqueueSnackbar("Панель не найдена", { variant: "error" });
      return;
    }

    let haveYear = false;
    setPanels(
      urls.map((id, index) => {
        const searchParams = new URLSearchParams(id.split("?")[1]);
        const year = searchParams.get("year");
        if (year) haveYear = true;
        return { id, project: searchParams.get("project"), year, report: searchParams.get("report"), value, number: index + 1 };
      })
    );
    setHaveYear(haveYear);
    setLoading(false);
  }, [api, registry]);

  React.useEffect(() => loadPanels(data), [loadPanels, data]);

  React.useEffect(() => {
    if (tableData.length === 0 || sessionRowsFired) return;
    
    setSessionRowsFired(true);
    if (!sessionRows) return;
    
    const parsed = JSON.parse(sessionRows);
    if (!Array.isArray(parsed) || parsed.length === 0) return;

    const filtered = parsed.filter((id) => panels.find((panel) => panel.id === id));
    changeRowsHandler(filtered);
  }, [tableData, panels])

  React.useEffect(() => {
    if (panels.length !== 1) return;
    handleDashboardOpen({ rows: [panels[0].id], valueOnly: true });
  }, [handleDashboardOpen, panels]);

  const schema = React.useMemo(
    () => ({
      value: "roles",
      type: "Table",
      data: {
        id: "panels-table",
        trDims: haveYear ? yearTrDims : trDims,
        fullCustomToolbar: true,
        toolbar: [
          [OpenIcon, handleDashboardOpen, TOOLBAR_DISABLE_CASES.more_than_zero, null, null, null, "Перейти"],
        ],
      },
    }),
    [handleDashboardOpen, haveYear]
  );

  const handleSelectedRowsChanged = React.useCallback((rows) => {
    sessionStorage.setItem(data.code, JSON.stringify(rows));
  }, [data]);

  const customTableDataGetter = React.useCallback(() => Array.isArray(panels) ? panels : [], [panels]);

  if (loading || panels.length === 1) return <ContourBackdrop />;

  return (
    <ActionDialog
      minHeight="60vh"
      width="1000px"
      maxWidth="1000px"
      onClose={onOk}
      open
      title="Панели"
    >
      <TableContainer>
        <TableEditor 
          setRowsHandler={setRowsHandler} 
          rowsHandler={handleSelectedRowsChanged} 
          customTableDataGetter={customTableDataGetter} 
          schema={schema} 
          tableDataHandler={setTableData}
          rerenderDeps={[panels]}
        />
      </TableContainer>
    </ActionDialog>
  );
}


const TableContainer = styled("div")`
  flex: 1;
  display: flex;
`;

export default React.memo(Panels);
